import type {
  GalleryEditRequest,
  GalleryImageViewRequest,
} from '../tools/gallery/editorjs-gallery-tool';
import type { Dispatch, RefObject, SetStateAction } from 'react';

import { useEffect } from 'react';

import type { EditorJsData } from '#pages/document/editorjs-types';

import AddToChatTune from '../tunes/editorjs-add-to-chat-tune';
import AiInstructionsTune, {
  type AiInstructionsEditRequest,
} from '../tunes/editorjs-ai-instructions-tune';

import { createSafeStrikethroughTool } from './strikethrough-list-range-trim';

/** Values read at call time by Editor.js tools so the instance is not recreated when props/context change. */
export type EditorDynamicConfig = {
  onUpload?: (file: File) => Promise<string>;
  onAddToChat?: (blockId: string) => void;
  isSourceVisible: (entryId: string) => boolean;
  setSourceVisible: (entryId: string, visible: boolean) => void;
  onRequestGalleryEdit?: (request: GalleryEditRequest) => void;
  onOpenGalleryImage?: (request: GalleryImageViewRequest) => void;
  onRequestAiInstructionsEdit?: (request: AiInstructionsEditRequest) => void;
};

export type UseEditorSetupParams = {
  holderRef: RefObject<HTMLDivElement | null>;
  editorRef: RefObject<any | null>;
  persistChainRef: RefObject<Promise<unknown>>;
  latestValueRef: RefObject<EditorJsData | null>;
  dynamicConfigRef: RefObject<EditorDynamicConfig>;
  commitEditorOutput: (data: EditorJsData) => void;
  schedulePersist: (run: () => Promise<void>) => Promise<unknown>;
  registerDocumentHeadingPortalTarget: (id: string, el: HTMLElement) => void;
  unregisterDocumentHeadingPortalTarget: (id: string) => void;
  registerFilesPortalTarget: (id: string, el: HTMLElement) => void;
  unregisterFilesPortalTarget: (id: string) => void;
  setDocumentHeadingPortalTargets: Dispatch<
    SetStateAction<Map<string, HTMLElement>>
  >;
  setFilesPortalTargets: Dispatch<SetStateAction<Map<string, HTMLElement>>>;
};

export function useEditorSetup({
  holderRef,
  editorRef,
  persistChainRef,
  latestValueRef,
  dynamicConfigRef,
  commitEditorOutput,
  schedulePersist,
  registerDocumentHeadingPortalTarget,
  unregisterDocumentHeadingPortalTarget,
  registerFilesPortalTarget,
  unregisterFilesPortalTarget,
  setDocumentHeadingPortalTargets,
  setFilesPortalTargets,
}: UseEditorSetupParams): void {
  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      if (!holderRef.current) return;

      try {
        const [
          { default: EditorJS },
          { default: CodeTool },
          HeaderModule,
          { default: ImageTool },
          { default: EditorjsList },
          { default: TableTool },
          { DocumentHeadingTool },
          { default: MermaidTool },
          { GalleryTool },
          { FilesTool },
          StrikethroughModule,
        ] = await Promise.all([
          import('@editorjs/editorjs'),
          import('@editorjs/code'),
          import('@editorjs/header'),
          import('@editorjs/image'),
          import('@editorjs/list'),
          import('@editorjs/table'),
          import('../tools/document-heading/editorjs-document-heading-tool'),
          import('../tools/mermaid/editorjs-mermaid-tool'),
          import('../tools/gallery/editorjs-gallery-tool'),
          import('../tools/files/editorjs-files-tool'),
          import('editorjs-strikethrough'),
        ]);

        if (cancelled || !holderRef.current) return;

        const hasUploader = Boolean(dynamicConfigRef.current.onUpload);

        const headerClass =
          typeof HeaderModule === 'function'
            ? HeaderModule
            : ((HeaderModule as any).default ?? HeaderModule);

        const StrikethroughTool =
          (StrikethroughModule as any)?.default ?? StrikethroughModule;

        const SafeStrikethroughTool =
          createSafeStrikethroughTool(StrikethroughTool);

        const inlineToolbarTools: string[] = [
          'link',
          'bold',
          'italic',
          // Editor.js strikethrough tool (wraps with <del class="cdx-strikethrough">).
          'strikethrough',
        ];

        const editor = new EditorJS({
          holder: holderRef.current,
          data: (latestValueRef.current as any) ?? { blocks: [] },
          autofocus: true,
          tools: {
            header: {
              class: headerClass,
              // Enables inline formatting toolbar on double-click/selection.
              inlineToolbar: inlineToolbarTools,
              config: {
                levels: [1, 2, 3],
                defaultLevel: 2,
              },
            },
            list: {
              class: EditorjsList,
              inlineToolbar: inlineToolbarTools,
            },
            table: {
              // @editorjs/table typings require `config` on constructor; Editor.js passes optional config.
              class: TableTool as any,
              inlineToolbar: inlineToolbarTools,
            },
            strikethrough: {
              class: SafeStrikethroughTool as any,
            },
            code: CodeTool,
            image: {
              class: ImageTool,
              ...(hasUploader
                ? {
                    config: {
                      uploader: {
                        async uploadByFile(file: File) {
                          const upload = dynamicConfigRef.current.onUpload;
                          if (!upload) {
                            return { success: 0 };
                          }
                          const url = await upload(file);
                          return {
                            success: 1,
                            file: {
                              url,
                            },
                          };
                        },
                      },
                    },
                  }
                : {}),
            },
            mermaid: {
              class: MermaidTool,
              config: {
                getShowSource: (entryId: string) =>
                  dynamicConfigRef.current.isSourceVisible(entryId),
                setShowSource: (entryId: string, visible: boolean) =>
                  dynamicConfigRef.current.setSourceVisible(entryId, visible),
              },
            },
            documentHeading: {
              class: DocumentHeadingTool,
              inlineToolbar: inlineToolbarTools,
              config: {
                registerPortalTarget: registerDocumentHeadingPortalTarget,
                unregisterPortalTarget: unregisterDocumentHeadingPortalTarget,
              },
            },
            files: {
              class: FilesTool,
              config: {
                registerFilesPortalTarget,
                unregisterFilesPortalTarget,
              },
            },
            gallery: {
              class: GalleryTool,
              config: {
                onRequestGalleryEdit: (request: GalleryEditRequest) => {
                  dynamicConfigRef.current.onRequestGalleryEdit?.(request);
                },
                onOpenGalleryImage: (request: GalleryImageViewRequest) => {
                  dynamicConfigRef.current.onOpenGalleryImage?.(request);
                },
              },
            },
            addToChatTune: {
              class: AddToChatTune,
              config: {
                onAddToChatRequested: (blockId: string) => {
                  dynamicConfigRef.current.onAddToChat?.(blockId);
                },
              },
            },
            aiInstructions: {
              class: AiInstructionsTune,
              config: {
                onRequestAiInstructionsEdit: (
                  request: AiInstructionsEditRequest,
                ) => {
                  dynamicConfigRef.current.onRequestAiInstructionsEdit?.(
                    request,
                  );
                },
              },
            },
          },
          tunes: ['addToChatTune', 'aiInstructions'],
          onChange(api) {
            void schedulePersist(async () => {
              const data = (await api.saver.save()) as EditorJsData;
              commitEditorOutput(data);
            });
          },
        });

        editorRef.current = editor;
      } catch {
        if (holderRef.current) {
          holderRef.current.innerText =
            'Failed to load editor. Check console for details.';
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      persistChainRef.current = Promise.resolve();
      const editor = editorRef.current;
      if (editor) {
        void editor.isReady
          .then(() => {
            editor.destroy();
            editorRef.current = null;
          })
          .catch(() => {
            editorRef.current = null;
          });
      }
      setDocumentHeadingPortalTargets(new Map());
      setFilesPortalTargets(new Map());
    };
  }, [
    commitEditorOutput,
    registerDocumentHeadingPortalTarget,
    registerFilesPortalTarget,
    schedulePersist,
    unregisterDocumentHeadingPortalTarget,
    unregisterFilesPortalTarget,
  ]);
}
